import { BookingReason } from './BookingReason'
import { Event } from './Event'

export type BookingPaymentRequirement = (
    | {
        kind: 'card'
        amount: number
        isPerCover: boolean
        cutOffHours: number | null
    }
    | {
        kind: 'deposit'
        amount: number
        perCoverAmount: number | null
        refundCutOffHours: number | null
        isPaidUpsell?: boolean
    }

// Order money is collected upfront; a card requirement's amount remains its potential later fee.
) & { orderAmount?: number; depositAmount?: number }

export class BookingSlotOption {
    constructor(
        public partySize: number,
        public durationMinutes: number,
        public allTableCount: number,
        public reservableTableCount: number,
        public reasonId: string | null,
        public eventId: string | null,
        public cancellationChargeAmount: number | null,
        public cancellationChargeIsPerCover: boolean,
        public cancellationChargeCutOffHours: number | null,
        public depositAmount: number | null,
        public depositAmountIsPerCover: boolean,
        public depositRefundCutOffHours: number | null
    ) { }

    isReservableWithConfiguration(
        partySize: number,
        reason: BookingReason | null,
        event: Event | null
    ): boolean {
        if (!this.matchesConfiguration(partySize, reason, event)) {
            return false
        }
        return this.reservableTableCount > 0
    }

    isWaitlistOnlyWithConfiguration(
        partySize: number,
        reason: BookingReason | null,
        event: Event | null
    ): boolean {
        if (!this.matchesConfiguration(partySize, reason, event)) {
            return false
        }
        return this.allTableCount > 0 && this.reservableTableCount === 0
    }

    private matchesConfiguration(
        partySize: number,
        reason: BookingReason | null,
        event: Event | null
    ): boolean {
        const reasonIsNotReservable = reason !== null && this.reasonId !== reason.id
        if (reasonIsNotReservable) {
            return false
        }
        const eventIsNotReservable = event !== null && this.eventId !== event.id
        if (eventIsNotReservable) {
            return false
        }
        const standardBookingIsNotReservable = (reason === null && event === null)
            && (this.reasonId !== null || this.eventId !== null)
        if (standardBookingIsNotReservable) {
            return false
        }
        const isOfPartySize = partySize === this.partySize
        if (!isOfPartySize) {
            return false
        }
        return true
    }

    isReservableWithAnyReason(
        partySize: number,
        event: Event | null
    ): boolean {
        const eventIsNotReservable = event !== null && this.eventId !== event.id
        if (eventIsNotReservable) {
            return false
        }
        if (this.reasonId === null) {
            return false
        }
        const isOfPartySize = partySize === this.partySize
        if (!isOfPartySize) {
            return false
        }
        const hasReservableTables = this.reservableTableCount > 0
        if (!hasReservableTables) {
            return false
        }
        return true
    }

    get depositChargeAmount(): number | null {
        if (this.depositAmount === null) {
            return null
        }
        if (this.depositAmountIsPerCover) {
            return this.depositAmount * this.partySize
        } else {
            return this.depositAmount
        }
    }

    get paymentRequirement(): BookingPaymentRequirement | null {
        if (this.cancellationChargeAmount !== null) {
            return {
                kind: 'card',
                amount: this.cancellationChargeAmount,
                isPerCover: this.cancellationChargeIsPerCover,
                cutOffHours: this.cancellationChargeCutOffHours,
            }
        }
        const depositAmount = this.depositChargeAmount
        return depositAmount === null
            ? null
            : {
                kind: 'deposit',
                amount: depositAmount,
                perCoverAmount: this.depositAmountIsPerCover
                    ? this.depositAmount
                    : null,
                refundCutOffHours: this.depositRefundCutOffHours,
            }
    }
}
